import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

import ActionButton from '@/components/Button/ActionButton';
import { Spacer, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import { Separator } from '@/components/ui/separator';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';

import { useAllowIndividualRequestOperationPackageQuery } from '../../-query/useAllowIndividualRequestOperationPackageQuery';

import { ApplicationOperationPackageEligibleCards } from './-components/ApplicationOperationPackageEligibleCards';
import { ApplicationOperationPackagePatternCards } from './-components/ApplicationOperationPackagePatternCards';

export const Route = createFileRoute(
  '/_individual/individual/operation-package/$id/',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data } = useAllowIndividualRequestOperationPackageQuery(id);
  const hasEligibleTarget = data.packageType === 'AppUserCreation';

  return (
    <>
      {hasEligibleTarget ? (
        <EligibleTargetApplication id={id} name={data.name} />
      ) : (
        <DefaultApplication id={id} name={data.name} />
      )}
    </>
  );
}

/**
 * 対象を選択してもらうタイプの手続き（Step 1: ソフトウェア選択）
 */
const EligibleTargetApplication = ({
  id,
  name,
}: {
  id: string;
  name: string;
}) => {
  const navigate = Route.useNavigate();
  const tc = useCommonTranslation();

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.operationPackage',
  });
  const [targetId, setTargetId] = useState<string>('');

  const handleButtonClick = (targetId: string) => {
    setTargetId(targetId);
  };

  const handleNext = useCallback(() => {
    if (targetId) {
      navigate({
        to: `/individual/operation-package/${id}/target/${targetId}`,
      });
    }
  }, [id, targetId, navigate]);

  return (
    <VStack className="py-3 px-5 gap-3 items-start max-w-dialog-lg mx-auto w-full bg-neutral-fill min-h-full">
      <ActionButton
        icon="ChevronLeft"
        iconColor="muted"
        variant="ghost"
        className="p-0 text-neutral-muted-on-fill font-semibold h-fit"
        onClick={() => {
          navigate({ to: '/individual' });
        }}
      >
        {t('navigateToHome')}
      </ActionButton>
      <VStack className="gap-2">
        <Text variant="h2">{name}</Text>
        <Text variant="labelLarge">{t('page.selectPattern.description')}</Text>
      </VStack>
      <div className="py-3 w-full">
        <Separator />
      </div>
      <Spacer variant="s" />
      <ApplicationOperationPackageEligibleCards
        packageId={id}
        onClick={handleButtonClick}
      />
      <Spacer variant="s" />
      <ActionButton
        onClick={handleNext}
        className="w-full"
        disabled={!targetId}
      >
        {tc('action.next')}
      </ActionButton>
    </VStack>
  );
};

/**
 * Patternを明示的に選択してもらうタイプの手続き
 */
const DefaultApplication = ({ id, name }: { id: string; name: string }) => {
  const navigate = Route.useNavigate();

  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.operationPackage',
  });
  const tc = useCommonTranslation();

  const [selectedPackagePatternId, setSelectedPackagePatternId] =
    useState<string>('');

  const handleButtonClick = useCallback(() => {
    if (selectedPackagePatternId) {
      navigate({
        to: `/individual/operation-package/${id}/pattern/${selectedPackagePatternId}`,
      });
    }
  }, [id, selectedPackagePatternId, navigate]);

  return (
    <VStack className="p-6 gap-3 items-start max-w-dialog-lg mx-auto w-full bg-neutral-fill min-h-full">
      <ActionButton
        icon="ChevronLeft"
        variant="mutedGhost"
        size="sm"
        className="ml-[-8px]"
        onClick={() => {
          navigate({ to: '/individual' });
        }}
      >
        {t('navigateToHome')}
      </ActionButton>
      <VStack className="gap-2">
        <Text variant="h2">{name}</Text>
        <Text variant="labelLarge">{t('page.selectPattern.description')}</Text>
      </VStack>
      <div className="py-3 w-full">
        <Separator />
      </div>
      <Spacer variant="s" />
      <ApplicationOperationPackagePatternCards
        operationPackageId={id}
        selectedPackagePatternId={selectedPackagePatternId}
        setSelectedPackagePatternId={setSelectedPackagePatternId}
      />
      <Spacer variant="s" />
      <ActionButton
        onClick={handleButtonClick}
        className="w-full"
        disabled={!selectedPackagePatternId}
      >
        {tc('action.next')}
      </ActionButton>
    </VStack>
  );
};
